import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import {
  Activity,
  Filter,
  Map,
  RefreshCw,
  Settings,
  Shield,
} from "lucide-react";
import type { ComponentType, ReactNode } from "react";

export type HelpCategoryId =
  | "getting-started"
  | "sync"
  | "filters"
  | "maps"
  | "privacy"
  | "account";

export type HelpCategory = {
  id: HelpCategoryId;
  label: ReactNode;
  icon: ComponentType<{ className?: string }>;
  description: ReactNode;
};

export type HelpItem = {
  id: string;
  q: ReactNode;
  a: ReactNode;
  tags?: HelpCategoryId[];
  keywords?: string[];
};

const kw = (...tokens: string[]) =>
  tokens
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => s.toLowerCase());

export const CATEGORIES: HelpCategory[] = [
  {
    id: "getting-started",
    label: <Trans>Getting started</Trans>,
    icon: Activity,
    description: (
      <Trans>Connect Strava, import activities, and learn the basics.</Trans>
    ),
  },
  {
    id: "sync",
    label: <Trans>Sync & missing activities</Trans>,
    icon: RefreshCw,
    description: (
      <Trans>
        Troubleshoot delays, missing activities, and re-import behavior.
      </Trans>
    ),
  },
  {
    id: "filters",
    label: <Trans>Filters</Trans>,
    icon: Filter,
    description: (
      <Trans>
        Understand how filters affect charts, tables, calendar, and maps.
      </Trans>
    ),
  },
  {
    id: "maps",
    label: <Trans>Maps & heatmaps</Trans>,
    icon: Map,
    description: (
      <Trans>
        Heatmap behavior, GPS precision, and what to do when routes change.
      </Trans>
    ),
  },
  {
    id: "privacy",
    label: <Trans>Privacy & data</Trans>,
    icon: Shield,
    description: (
      <Trans>What we store, what we display, and how to stay in control.</Trans>
    ),
  },
  {
    id: "account",
    label: <Trans>Account & settings</Trans>,
    icon: Settings,
    description: (
      <Trans>Disconnect Strava, resync data, and manage your account.</Trans>
    ),
  },
];

export const HELP_ITEMS: HelpItem[] = [
  {
    id: "connect-strava",
    tags: ["getting-started"],
    keywords: kw(
      t`connect`,
      t`link account`,
      t`sign in`,
      t`sign in with strava`,
      t`strava login`,
      t`oauth`,
      t`authorize`,
      t`permissions`,
    ),
    q: <Trans>How do I connect Stratistics to Strava?</Trans>,
    a: (
      <Trans>
        Click “Sign in with Strava” and accept the requested permissions. You
        can also sign in with email first, then click “Connect to Strava”. After
        connecting, Stratistics will start importing your activities, and you’ll
        see charts, the activities table, calendar, and maps populate as data
        arrives.
      </Trans>
    ),
  },
  {
    id: "first-import-time",
    tags: ["getting-started", "sync"],
    keywords: kw(
      t`first import`,
      t`initial import`,
      t`takes a while`,
      t`slow`,
      t`history`,
      t`batch`,
      t`rate limit`,
      t`sync delay`,
    ),
    q: <Trans>Why is my first import taking a while?</Trans>,
    a: (
      <Trans>
        Initial imports can take time for large histories. We import in batches
        and may pause between batches to respect Strava API limits. You can
        continue using the app while data loads.
      </Trans>
    ),
  },
  {
    id: "missing-activities",
    tags: ["sync"],
    keywords: kw(
      t`missing activities`,
      t`not showing`,
      t`not all activities`,
      t`import missing`,
      t`private activity`,
      t`visibility`,
      t`date range`,
      t`activity type`,
      t`edited activity`,
      t`cropped`,
    ),
    q: <Trans>Some activities are missing—what should I check?</Trans>,
    a: (
      <>
        <ul className="ml-5 list-disc space-y-2">
          <li>
            <Trans>
              Activity filters: make sure your filters include the activities
              you expect.
            </Trans>
          </li>
          <li>
            <Trans>
              Recent edits: if the activity is new, or if you edited it on
              Strava, it may take a refresh/resync before changes appear.
            </Trans>
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "not-updating-immediately",
    tags: ["sync"],
    keywords: kw(
      t`not updating`,
      t`sync delay`,
      t`latest activity`,
      t`new activity`,
      t`rate limit`,
      t`api limit`,
      t`refresh`,
    ),
    q: <Trans>Why doesn’t my data update immediately?</Trans>,
    a: (
      <Trans>
        Strava enforces API rate limits. When limits are reached, sync may be
        delayed until the next window. In practice, this means a new activity
        may take a little while to appear, especially during high-traffic
        periods.
      </Trans>
    ),
  },
  {
    id: "quotas",
    tags: ["sync"],
    keywords: kw(
      t`quota`,
      t`rate limit`,
      t`api limit`,
      t`too many requests`,
      t`sync quota`,
      t`daily limit`,
      t`reached limit`,
      t`try again later`,
    ),
    q: (
      <Trans>
        I’m seeing errors saying I’ve reached my quota. What should I do?
      </Trans>
    ),
    a: (
      <Trans>
        Strava enforces API rate limits on third-party apps. To fairly share
        those limits across all Stratistics users, we cap the number of Strava
        API calls each user can trigger to a daily quota (currently 20 per day).
        <br />
        <br />
        If you reach this limit, syncing will pause temporarily. Simply try
        again the next day, when your quota resets automatically.
      </Trans>
    ),
  },

  {
    id: "force-refresh",
    tags: ["sync", "account"],
    keywords: kw(
      t`force refresh`,
      t`reimport`,
      t`re-import`,
      t`resync`,
      t`sync again`,
      t`renew permissions`,
      t`reconnect`,
    ),
    q: <Trans>How do I force a refresh / re-import?</Trans>,
    a: (
      <Trans>
        Click your profile in the left-side menu, then use the “Resync” action.
        If you still don’t see updates after some time, disconnect and reconnect
        Strava to renew permissions and trigger a new import.
      </Trans>
    ),
  },
  {
    id: "old-activities-edits",
    tags: ["sync"],
    keywords: kw(
      t`edited activity`,
      t`deleted activity`,
      t`old activity`,
      t`changes not reflected`,
      t`not updated`,
      t`sync window`,
      t`resync old activities`,
      t`missing edits`,
      t`activity changes`,
    ),
    q: (
      <Trans>
        I edited or deleted activities that were already imported. Will those
        changes be reflected in Stratistics?
      </Trans>
    ),
    a: (
      <Trans>
        During each sync, Stratistics checks your most recent activities on
        Strava (currently the last ~200). Any edits or deletions within that
        window will be detected and reflected automatically.
        <br />
        <br />
        If you need to update an older activity outside of this window, you can
        either delete and recreate your Stratistics account to trigger a full
        re-import, or contact us so we can help refresh the affected data.
      </Trans>
    ),
  },
  {
    id: "global-filters",
    tags: ["filters"],
    keywords: kw(
      t`global filters`,
      t`filters affect`,
      t`charts`,
      t`table`,
      t`calendar`,
      t`map`,
      t`consistent`,
    ),
    q: <Trans>Do filters affect all pages?</Trans>,
    a: (
      <Trans>
        Yes. Filters are global: charts, table, calendar, and map views should
        all reflect the same filter state so your analysis stays consistent.
      </Trans>
    ),
  },
  {
    id: "heatmap-precision",
    tags: ["maps"],
    keywords: kw(
      t`heatmap`,
      t`map`,
      t`missing detail`,
      t`coarse`,
      t`gps`,
      t`indoor`,
      t`precision`,
      t`route`,
    ),
    q: <Trans>My heatmap looks coarse / missing detail—why?</Trans>,
    a: (
      <Trans>
        By default, Strava provides a simplified version of your GPS tracks.
        Fetching all GPS points requires additional Strava API calls (coming
        soon).
      </Trans>
    ),
  },
  {
    id: "data-access",
    tags: ["privacy"],
    keywords: kw(
      t`data access`,
      t`what data`,
      t`permissions`,
      t`store`,
      t`map data`,
      t`routes`,
      t`oauth`,
    ),
    q: <Trans>What data does Stratistics access from Strava?</Trans>,
    a: (
      <Trans>
        Stratistics requests only what it needs to build your analytics:
        activity summaries (distance, time, elevation, type), timestamps, and
        map/route data when available. We do not need your Strava
        password—authorization is handled by Strava OAuth.
      </Trans>
    ),
  },
  {
    id: "who-can-see",
    tags: ["privacy"],
    keywords: kw(
      t`who can see`,
      t`visibility`,
      t`sharing`,
      t`public`,
      t`private`,
      t`security`,
      t`data`,
    ),
    q: <Trans>Who can see my activities and stats?</Trans>,
    a: (
      <Trans>
        By default, Stratistics is designed for you to view your own analytics.
        We’re building community heatmaps: only activities that are already
        public on Strava will be visible there. For details, see our Privacy
        page.
      </Trans>
    ),
  },
  {
    id: "disconnect",
    tags: ["account", "privacy"],
    keywords: kw(
      t`disconnect`,
      t`revoke access`,
      t`connected apps`,
      t`strava apps`,
      t`unlink`,
      t`remove`,
    ),
    q: <Trans>How do I disconnect Stratistics from Strava?</Trans>,
    a: (
      <Trans>
        You can revoke access from Strava’s “My Apps / Connected Apps” settings.
        After revoking, Stratistics will no longer be able to sync new data
        until you reconnect.
      </Trans>
    ),
  },
  {
    id: "delete-account",
    tags: ["account", "privacy"],
    keywords: kw(
      t`delete account`,
      t`delete data`,
      t`remove data`,
      t`retention`,
      t`privacy`,
      t`erase`,
    ),
    q: <Trans>How do I delete my account and data?</Trans>,
    a: (
      <Trans>
        You can delete your account from your profile settings. You can also
        review how we handle deletion and retention on our Privacy page.
      </Trans>
    ),
  },
];
